import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  EnrollmentNumberSettingRequest,
  EnrollmentNumberSettingState,
  initialEnrollmentNumberSettingState,
} from '../entities/enrollment-number-setting.model';
import { EnrollmentNumberSettingService } from '../infra/enrollment-number-setting.service';

const OPS = ['load', 'update'] as const;

/**
 * Per-tenant enrollment-number mask (SchoolService, PR #50). A single record:
 * `load` reads the effective mask (tenant's or the service default) and `update`
 * persists a new mask — which only affects FUTURE first-time assignments;
 * existing numbers are permanent.
 *
 * Refusals come back as the 007 envelope (422) whose `message` is already
 * localized; the store forwards the raw error to `ErrorStore.setHttpError`,
 * which surfaces `message` verbatim — the front never builds error text.
 */
export const EnrollmentNumberSettingStore = signalStore(
  { providedIn: 'root' },
  withState<EnrollmentNumberSettingState>(initialEnrollmentNumberSettingState),
  withReqState(...OPS),
  withMethods(
    (
      store,
      service = inject(EnrollmentNumberSettingService),
      errorStore = inject(ErrorStore),
    ) => ({
      load: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap(() =>
            service.get().pipe(
              tapResponse({
                next: (setting) => patchState(store, { setting }, setPropLoaded('load')),
                error: (e: HttpErrorResponse) =>
                  patchState(store, setPropError('load', e)),
              }),
            ),
          ),
        ),
      ),

      update: rxMethod<EnrollmentNumberSettingRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('update'))),
          switchMap((body) =>
            service.update(body).pipe(
              tapResponse({
                next: (setting) => patchState(store, { setting }, setPropLoaded('update')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('update', e));
                  errorStore.setHttpError(e);
                },
              }),
            ),
          ),
        ),
      ),

      reset(): void {
        patchState(store, initialEnrollmentNumberSettingState, setPropInit(...OPS));
      },
    }),
  ),
);
